import type { UI } from '@/v2/types';
import { useMemo } from 'react';

import useCampaignPills from '@/hooks/useCampaignPills';
import createFlightCampaignInclusion from '@/v2/utils/campaign/createFlightCampaignInclusion';

export type UseCampaignBrandingProps = {
  campaign?: UI.Campaign | null;
  flightPromoCodes?: Set<string> | undefined;
  isCampaignAvailable: boolean;
};

type CampaignPills =
  | {
      campaignPills: Record<
        string,
        {
          flightPillText: string;
          termsConditions: string;
        }
      >;
    }
  | undefined;

const useFlightInclusion = ({
  campaign,
  flightPromoCodes,
  isCampaignAvailable,
}: UseCampaignBrandingProps) => {
  const { campaignPills }: CampaignPills = useCampaignPills();

  return useMemo(() => {
    const title = campaign?.sashing?.flightPillText;
    const terms = campaign?.homePageHero?.termsConditions ?? undefined;

    // Prioritise provided campaign
    // else, fallback to use codes from flight options
    if (isCampaignAvailable && title) {
      return createFlightCampaignInclusion({ title, terms });
    }

    if (!campaignPills || !flightPromoCodes) {
      return undefined;
    }

    const promoCode = Array.from(flightPromoCodes).find(
      (promoCode) =>
        campaignPills[promoCode]?.flightPillText &&
        campaignPills[promoCode]?.termsConditions,
    );

    const pillData = promoCode ? campaignPills[promoCode] : undefined;

    return pillData
      ? createFlightCampaignInclusion({
          terms: pillData.termsConditions,
          title: pillData.flightPillText,
        })
      : undefined;
  }, [campaign, campaignPills, flightPromoCodes, isCampaignAvailable]);
};

export default useFlightInclusion;
